import { useState, useEffect, useRef } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import TranscriptionConsentNotice from "../../../components/consultation/TranscriptionConsentNotice";
import SessionTranscript from "../../../components/consultation/SessionTranscript";
import { Card, CardContent } from "../../../components/ui/CCard";
import { Input } from "../../../components/ui/Input";
import { Label } from "../../../components/ui/Label";
import { Textarea } from "../../../components/ui/Textarea";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import api from "../../../api";
import PatientTakeawayPreview from "../../../components/consultation/PatientTakeawayPreview";
import { ownSummaryText, MIN_OWN_SUMMARY_CHARS } from "../../../utils/patientSummary";
import { X, Mic, MicOff, History, User, ChevronRight, ChevronDown, MessageSquare, UploadCloud, FileText, Download, Loader2, Trash2, Sparkles, Printer, Wand2, AlertTriangle, ClipboardList, Pill, HeartHandshake, PenLine, Save, Send, BarChart3, BellRing, Video, Target, BookOpenCheck, CheckCircle2 } from "lucide-react";
import HistoryModal from "../../../components/consultation/HistoryModal";
import VideoCallModal from "../appointment/VideoCallModal";
import ConsultationSidePanel from "../../../components/consultation/ConsultationSidePanel";
import PrescriptionTable from "../../../components/consultation/PrescriptionTable";
import TestsTable from "../../../components/consultation/TestsTable";
import FollowUpTable from "../../../components/consultation/FollowUpTable";
import ClinicalTypeaheadTextarea from "../../../components/consultation/ClinicalTypeaheadTextarea";
import RiskAssessmentSection, { emptyRisk, riskStatus } from "../../../components/consultation/RiskAssessmentSection";
import CrisisButton from "../../../components/consultation/CrisisButton";
import TreatmentPlanPanel from "../../../components/clinical/TreatmentPlanPanel";
import HomeworkPanel from "../../../components/clinical/HomeworkPanel";
import PreviousSessionCarryover from "../../../components/consultation/PreviousSessionCarryover";
import { useSessionAssist, SessionDraftBox } from "../../../components/consultation/SessionDrafts";
import FormulationPanel from "../../../components/clinical/FormulationPanel";
import NoteTemplateSections, { NoteTemplatePicker, NoteFormatPrompt, useSessionNoteFormat, rememberSessionNoteFormat, noteFormatNeeded } from "../../../components/consultation/NoteTemplateSection";
import useFeatures from "../../../hooks/useFeatures";
import { printMedicalPrescription, getPrescriptionHeaderSettings } from "../../../utils/prescriptionPrinter";


const SHOW_SESSION_DETAILS = false; // Date/Time/Duration panel hidden per product request
const SHOW_AI_FEEDBACK = false; // "What Went Well" AI feedback hidden for now (2026-10-24); data is still stored

export default function ConsultationNew() {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState([]);
  const location = useLocation();
  const [history, setHistory] = useState([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);

  const patientFromAppointment = location.state?.patient_id || null;
  const meetingId = location.state?.id || null;
  const [Error, setError] = useState("")
  const [loading, setLoading] = useState(true);
  const { id } = useParams(); // check if we are in edit mode

  // location.state is the full appointment object from Appointment.js's
  // listing (see confirmStartConsultation there) - it already carries
  // meeting_room_name/duration_minutes/date/time/appttype, so no extra
  // fetch is needed here the way ConsultationEdit.js needs one.
  const [consultationStatus, setConsultationStatus] = useState(location.state?.consultation_status || "in_progress");
  const [videoCallOpen, setVideoCallOpen] = useState(false);
  // "side" while the call is docked on the right - the page makes room for it.
  const [videoLayout, setVideoLayout] = useState(null);
  // Until the doctor first opens the call, Join Meeting is the page's main
  // action (pulsing button + prompt above the notes).
  const [hasJoinedCall, setHasJoinedCall] = useState(false);
  const openVideoCall = () => { setHasJoinedCall(true); setVideoCallOpen(true); };
  // Re-render every 30s so Join Meeting appears (and the "opens at" note
  // goes away) when the call window opens, without a page reload.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    if ((location.state?.appttype || "").toLowerCase() !== "online") return undefined;
    const timer = setInterval(() => setClockTick((n) => n + 1), 30000);
    return () => clearInterval(timer);
  }, [location.state?.appttype]);

  const isMeetWindowOpen = () => {
    const appt = location.state;
    if (!appt?.date || !appt?.time) return false;
    const start = new Date(`${appt.date}T${appt.time}`);
    if (isNaN(start.getTime())) return false;
    const minutesUntilStart = (start - new Date()) / 60000;
    const end = new Date(start.getTime() + (appt.duration_minutes || 30) * 60000);
    return minutesUntilStart <= 15 && new Date() < end;
  };
  const isOnlineVisit =
    (location.state?.appttype || "").toLowerCase() === "online" &&
    Boolean(location.state?.meeting_room_name) &&
    consultationStatus !== "completed";
  const canJoinMeeting = isOnlineVisit && isMeetWindowOpen();
  const meetingStart = (() => {
    const appt = location.state;
    const start = appt?.date && appt?.time ? new Date(`${appt.date}T${appt.time}`) : null;
    return start && !isNaN(start.getTime()) ? start : null;
  })();
  const meetingNotOpenYet = isOnlineVisit && !canJoinMeeting && meetingStart && meetingStart > new Date();

  const getSavedPrintMode = () => {
    try {
      const saved = localStorage.getItem("default_prescription_print_mode");
      if (saved) return saved;
      const cfg = getPrescriptionHeaderSettings();
      return cfg.default_hide_header ? "preprinted" : "full";
    } catch (e) {
      return "full";
    }
  };

  // inside ConsultationNew
  const [consultationId, setConsultationId] = useState(null); // track if editing
  const features = useFeatures();
  const [riskData, setRiskData] = useState(emptyRisk());
  const [noteTemplates, setNoteTemplates] = useState([]);
  useEffect(() => {
    if (features.has("note_templates")) api.getNoteTemplates().then((r) => setNoteTemplates(r.data || [])).catch(() => setNoteTemplates([]));
  }, [features.ready]); // eslint-disable-line
  // Same 3-question structure as ConsultationEdit.js's reflection tab -
  // serialized into consultationData.selfreflection as JSON at save time
  // (see handleSave), parsed back out on load, exactly mirroring Edit's
  // own save/load shape so a reflection started here reads correctly there.
  const [reflectionEffective, setReflectionEffective] = useState("");
  const [reflectionStuck, setReflectionStuck] = useState("");
  const [reflectionNextTime, setReflectionNextTime] = useState("");
  const [reports, setReports] = useState([]);
  const [uploadingReports, setUploadingReports] = useState(false);
  const [reportName, setReportName] = useState("");
  const [linkedTestName, setLinkedTestName] = useState("");
  const [isPrintMenuOpen, setIsPrintMenuOpen] = useState(false);
  const [selectedPrintMode, setSelectedPrintMode] = useState(getSavedPrintMode);
  // Visit Summary printouts leave the diagnosis off unless asked for each time.
  const [summaryIncludeDiagnosis, setSummaryIncludeDiagnosis] = useState(false);
  const [prescriptionHeaderConfig, setPrescriptionHeaderConfig] = useState(null);
  const [doctorDetails, setDoctorDetails] = useState(null);
  const [generatingRx, setGeneratingRx] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [patientMessageDraft, setPatientMessageDraft] = useState("");
  const [generatingPatientMessage, setGeneratingPatientMessage] = useState(false);
  const [sendingPatientMessage, setSendingPatientMessage] = useState(false);
  const [patientMessageToast, setPatientMessageToast] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("auth");
    if (token) {
      try {
        const decoded = jwtDecode(JSON.parse(token).token);
        setCurrentUserId(decoded.sub?.user_id ?? null);
      } catch (e) {
        console.error("Failed to decode token", e);
      }
    }
  }, []);

  const canDoctorPrescribeMedicine = (doc) => {
    if (!doc) return true;
    if (doc.canPrescribeMedicine !== undefined && doc.canPrescribeMedicine !== null) {
      return Boolean(doc.canPrescribeMedicine);
    }
    const spec = (doc.specialized || doc.specialization || "").toLowerCase();
    const nonPrescribing = ["psychologist", "psychotherapy", "counselor", "counsellor", "nutrition", "dietetics"];
    if (nonPrescribing.some(kw => spec.includes(kw))) {
      return false;
    }
    return true;
  };

  // Fresh from the server (same as ConsultationEdit.js): the cached copy's
  // S3 logo link expires, or may never have been cached on this browser.
  useEffect(() => {
    try {
      setPrescriptionHeaderConfig(getPrescriptionHeaderSettings());
    } catch (e) {
      console.error(e);
    }
    api.getOrgConfig("prescription_header")
      .then((res) => {
        if (res.data?.config_data) {
          setPrescriptionHeaderConfig(res.data.config_data);
          localStorage.setItem("org_prescription_header", JSON.stringify(res.data.config_data));
        }
      })
      .catch(() => {});
  }, []);

  const [consultationData, setConsultationData] = useState({
    meetingID: meetingId,
    // Placeholder until the doctor loads and the effect below resolves the
    // real value from their specialization - this is never user-selectable
    // (see the effect near the doctor-details fetch), so this initial value
    // barely matters beyond avoiding a brief flash of the wrong field set.
    consultation_type: "mental_health",
    sessiondate: "",
    sessiontime: "",
    sessionduration: "",
    // Mental Health Fields
    summary: "",
    mood: "",
    mainthemes: "",
    resistanceorengagement: "",
    interventions: "",
    interventionseffectiveness: "",
    updatedhypothesis: "",
    sessionoutcome: "",
    nextplan: "",
    selfreflection: "",
    what_went_well: "",
    // GP Fields
    gp_symptoms: "",
    gp_vitals: "",
    gp_diagnosis: "",
    gp_prescription: "",
    gp_advice: "",
    gp_tests: "",
    follow_up_items: [],
  });

  // Tests the AI most recently suggested via "Generate with AI" - kept only
  // in memory (not persisted) so we can flag on-screen if a test it marked
  // required_before_medicine hasn't actually been added to gp_tests yet.
  // Not a DB field: this is a "did you address what was just suggested"
  // runtime check, not a stored fact about the consultation.
  const [aiSuggestedTests, setAiSuggestedTests] = useState([]);

  // --- Voice Transcription State ---
  const [transcript, setTranscript] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false); // Collapsible state

  const recognitionRef = useRef(null);
  const bufferRef = useRef("");
  const navigate = useNavigate();
  const bottomRef = useRef(null); // auto scroll ref

  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        if (bufferRef.current) {
          setTranscript((prev) => prev + bufferRef.current + " ");
          bufferRef.current = "";
        }
      }, 10000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // Auto-scroll transcript
  useEffect(() => {
    if (isTranscriptOpen && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcript, isTranscriptOpen]);

  // --- Modal Logic ---
  const handleStartConsultation = async () => {
    try {
      const res = await api.fetchcreditofloggedinuser();
      // Per-session AI orgs pay per session instead of spending credits.
      if (res.data?.ai_billing_mode === "per_session") {
        if (res.data?.ai_paused) {
          alert("AI features are paused because of unpaid AI session charges. Please ask your admin to clear the AI dues from Finance.");
        } else {
          startConsultation();
        }
      } else if (res.data?.available_credit >= 1) {
        startConsultation();
      } else {
        alert("Your credit limit is low. Please recharge to use AI feature.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startConsultation = () => {
    if (selectedPatient?.patienttype === "Virtual") {
      handleVirtualStart()
    } else {
      if (isRecording) {
        stopRecording();
      } else {
        setIsTranscriptOpen(true);
        startRecording();
      }
    }
  }

  const startRecording = () => {
    if (!("webkitSpeechRecognition" in window || "SpeechRecognition" in window)) {
      alert("Speech Recognition not supported in this browser.");
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = selectedPatient.lang || "en-US";

    recognition.onresult = (event) => {
      let finalTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript + " ";
        }
      }
      if (finalTranscript) {
        bufferRef.current += finalTranscript;
      }
    };

    recognition.onend = () => {
      if (recognitionRef.current) {
        if (isRecording) {
          recognition.start();
        }
      }
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
    };

    recognition.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
      if (bufferRef.current) {
        setTranscript((prev) => prev + bufferRef.current + " ");
        bufferRef.current = "";
      }
    }
  };

  const handleVirtualStart = async () => {
    let result = await handleSave();
    if (result === false) return;

    const chatState = {
      patient_profile: selectedPatient,
      consultation_id: result.id,
      doctor_id: location.state.doctor_id,
      current_session_no: history.length + 1,
    };
    localStorage.setItem("chatState", JSON.stringify(chatState));
    startRecording();
    navigate("/chat", { state: chatState });
  };

  const handleChange = (e) => {
    setConsultationData({ ...consultationData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (patientFromAppointment) {
      const loadPatients = async () => {
        try {
          const res = await api.getPatient(patientFromAppointment);
          setSelectedPatient(res.data);
        } catch (err) {
          setError("Failed to load patient details.");
        }
      };
      loadPatients();

      const loadPatientHistory = async () => {
        try {
          const res = await api.getAllConsultationsOfPatient(patientFromAppointment);
          setHistory(res.data || []);
        } catch (err) {
          console.error("Failed to load history.");
        }
      };
      loadPatientHistory();
    }
  }, [patientFromAppointment]);

  useEffect(() => {
    const doctorId = location.state?.doctor_id;
    if (!doctorId) return;
    (async () => {
      try {
        const res = await api.getDoctor(doctorId);
        setDoctorDetails(res.data);
      } catch (err) {
        console.error("Failed to load doctor details:", err);
      }
    })();
  }, [location.state?.doctor_id]);

  // Mental Health vs General Medicine is entirely determined by the doctor's
  // specialization (configured server-side, per specialization, in the
  // Specializations table) - never a choice the person filling this form
  // makes. This overwrites the placeholder default above the moment real
  // doctor data arrives; the backend also derives this independently at
  // save time, so it's enforced even if this effect never ran.
  useEffect(() => {
    if (doctorDetails?.default_consultation_type) {
      setConsultationData((prev) => ({ ...prev, consultation_type: doctorDetails.default_consultation_type }));
    }
  }, [doctorDetails]);

  const formatAIPrescriptionForTable = (prescriptionItems) => {
    return (prescriptionItems || [])
      .filter((m) => m && m.medicine_name)
      .map((m) => {
        const name = [m.medicine_name, m.strength].filter(Boolean).join(" ").trim();
        const timing = m.timing || "After Food";
        const dose = m.dose || "1-0-1";
        const duration = m.duration || "";
        return [name, timing, dose, duration].join(" | ");
      })
      .join("\n");
  };

  const formatAIAdviceForTextarea = (adviceItems) => {
    return (adviceItems || []).filter(Boolean).map((a) => `• ${a}`).join("\n");
  };

  const formatAITestsForTable = (testItems) => {
    return (testItems || [])
      .filter((t) => t && t.test_name)
      .map((t, idx) => {
        const parts = [`${idx + 1}. ${t.test_name.trim()}`];
        if (t.reason && t.reason.trim()) parts.push(t.reason.trim());
        return parts.join(" | ");
      })
      .join("\n");
  };

  const handleGenerateAIPrescription = async () => {
    if (!canDoctorPrescribeMedicine(doctorDetails)) {
      alert("Medicine prescription is restricted for this doctor's specialization (e.g. Clinical Psychologist cannot prescribe medicine).");
      return;
    }
    if (!consultationData.gp_diagnosis || !consultationData.gp_diagnosis.trim()) {
      alert("Please enter a Clinical Diagnosis / Assessment before generating an AI prescription.");
      return;
    }
    const specialization = doctorDetails?.specialized;
    if (!specialization) {
      alert("Doctor specialization not found. Please set a specialization on the doctor's profile before using AI generation.");
      return;
    }
    if (
      (consultationData.gp_prescription || consultationData.gp_advice) &&
      !window.confirm("This will replace the current prescription and advice with AI-generated content. Continue?")
    ) {
      return;
    }

    setGeneratingRx(true);
    try {
      const age = calculateAge(selectedPatient?.dob);
      const res = await api.generateDoctorPrescription({
        specialization,
        complaints_history: consultationData.gp_symptoms,
        vitals_examination: consultationData.gp_vitals,
        diagnosis: consultationData.gp_diagnosis,
        patient_age: age === "N/A" ? null : age,
        patient_sex: selectedPatient?.gender,
      });
      setConsultationData((prev) => ({
        ...prev,
        gp_prescription: formatAIPrescriptionForTable(res.data?.prescription),
        gp_advice: formatAIAdviceForTextarea(res.data?.advice),
        gp_tests: formatAITestsForTable(res.data?.tests),
      }));
      setAiSuggestedTests(res.data?.tests || []);
    } catch (err) {
      console.error("AI prescription generation failed:", err);
      alert(err.response?.data?.error || "Failed to generate AI prescription. Please try again.");
    } finally {
      setGeneratingRx(false);
    }
  };

  // Rewrites the current "Shared with Patient" advice into a warm,
  // second-person draft - never touches gp_advice itself, just prefills a
  // separate preview the doctor can edit before sending.
  const handleGeneratePatientMessage = async () => {
    if (!consultationData.gp_advice || !consultationData.gp_advice.trim()) return;
    setGeneratingPatientMessage(true);
    try {
      const res = await api.rewriteAdviceForPatient({
        advice: consultationData.gp_advice,
        patient_name: selectedPatient?.name,
        doctor_name: doctorDetails?.name,
      });
      setPatientMessageDraft(res.data?.patient_summary || "");
    } catch (err) {
      console.error("Failed to generate patient message", err);
      setPatientMessageToast({ type: "error", text: "Failed to generate message. Please try again." });
      setTimeout(() => setPatientMessageToast(null), 5000);
    } finally {
      setGeneratingPatientMessage(false);
    }
  };

  const handleSendPatientMessage = async () => {
    if (!patientMessageDraft.trim() || !meetingId) return;
    setSendingPatientMessage(true);
    try {
      await api.sendAppointmentMessage(meetingId, { message: patientMessageDraft.trim() });
      setPatientMessageDraft("");
      setPatientMessageToast({ type: "success", text: "Message sent to patient." });
      setTimeout(() => setPatientMessageToast(null), 5000);
    } catch (err) {
      console.error("Failed to send patient message", err);
      setPatientMessageToast({ type: "error", text: "Failed to send message. Please try again." });
      setTimeout(() => setPatientMessageToast(null), 5000);
    } finally {
      setSendingPatientMessage(false);
    }
  };

  const handleSave = async (showNotification = true, markCompleted = false) => {
    // Like the risk assessment: a mental-health note needs a format before it's
    // completed, when the clinic has no default and none was picked.
    if (markCompleted && await noteFormatNeeded(meetingId, consultationData.template_code)) {
      setAskNoteFormat(true);
      return false;
    }
    try {
      const payload = {
        patient_id: selectedPatient?.id,
        doctor_id: location.state?.doctor_id,
        meeting_id: meetingId,
        ...consultationData,
        selfreflection: JSON.stringify({
          effective: reflectionEffective,
          stuck: reflectionStuck,
          next_time: reflectionNextTime,
        }),
        transcript,
      };
      if (markCompleted) {
        payload.status = "completed";
      }
      if (showRisk) {
        const rs = riskStatus(riskData);
        if (rs === "complete") payload.risk_assessment = riskData;
        else if (rs === "incomplete" && markCompleted) {
          alert("Finish the risk assessment (Clinical note tab) before completing the consultation, or clear it.");
          return false;
        }
      }

      let res;
      if (consultationId) {
        res = await api.updateConsultation(consultationId, payload);
      } else {
        res = await api.createConsultation(payload);
        setConsultationId(res.data.id);
      }
      if (markCompleted) {
        setConsultationStatus("completed");
        if (showNotification) alert("Consultation completed.");
        // Carry on in the saved consultation (Saved state, signing, session
        // feedback) instead of a form that still offers Complete. Not during a
        // call: leaving this page would end it.
        if (!videoCallOpen && res.data?.id) navigate(`/consultation/${res.data.id}`, { replace: true });
      } else if (showNotification) {
        alert(consultationId ? "Consultation updated successfully!" : "Consultation saved successfully!");
      }
      return res.data;
    } catch (err) {
      console.error("Save consultation error:", err);
      if (showNotification) alert(err?.response?.data?.error || "Failed to save consultation. Please try again.");
      return false;
    }
  };

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const res = await api.getConsultation(id);
          const data = res.data;
          setConsultationId(data.id);
          setConsultationData({
            consultation_type: data.consultation_type || "mental_health",
            sessiondate: data.sessiondate || "",
            sessiontime: data.sessiontime || "",
            sessionduration: data.sessionduration || "",
            summary: data.summary || "",
            mood: data.mood || "",
            mainthemes: data.mainthemes || "",
            resistanceorengagement: data.resistanceorengagement || "",
            interventions: data.interventions || "",
            interventionseffectiveness: data.interventionseffectiveness || "",
            updatedhypothesis: data.updatedhypothesis || "",
            sessionoutcome: data.sessionoutcome || "",
            nextplan: data.nextplan || "",
            selfreflection: data.selfreflection || "",
            what_went_well: data.what_went_well || "",
            gp_symptoms: data.gp_symptoms || "",
            gp_vitals: data.gp_vitals || "",
            gp_diagnosis: data.gp_diagnosis || "",
            gp_prescription: data.gp_prescription || "",
            gp_advice: data.gp_advice || "",
            gp_tests: data.gp_tests || "",
            follow_up_items: data.follow_up_items || [],
          });

          // Same JSON-or-plain-text fallback as ConsultationEdit.js's own load.
          if (data.selfreflection) {
            try {
              const parsed = typeof data.selfreflection === "string"
                ? JSON.parse(data.selfreflection)
                : data.selfreflection;
              if (parsed && typeof parsed === "object") {
                setReflectionEffective(parsed.effective || "");
                setReflectionStuck(parsed.stuck || "");
                setReflectionNextTime(parsed.next_time || "");
              } else {
                setReflectionEffective(data.selfreflection);
              }
            } catch (e) {
              setReflectionEffective(data.selfreflection);
            }
          }

          setTranscript(data.transcript || "");
          if (data.transcript) setIsTranscriptOpen(true);

          try {
            const reportsRes = await api.getConsultationReports(data.id);
            setReports(reportsRes.data || []);
          } catch (e) {
            console.error("Failed to load reports", e);
          }
        } catch (err) {
          console.error("Failed to fetch consultation:", err);
          alert("Error loading consultation");
        }
      })();
    }
  }, [id]);

  function getLanguage(code) {
    const langs = {
      "en-US": "English", "hi-IN": "Hindi", "bn-IN": "Bengali",
      "ta-IN": "Tamil", "te-IN": "Telugu", "mr-IN": "Marathi",
      "kn-IN": "Kannada", "ur-IN": "Urdu", "sa-IN": "Sanskrit",
      "gu-IN": "Gujarati", "as-IN": "Assamese", "ml-IN": "Malayalam",
      "or-IN": "Odia", "pa-IN": "Punjabi"
    };
    return langs[code] || "English";
  }

  function handlePatientEdit(data) {
    let patient_data = { ...data, referee_url: "/Consultation-new/" };
    navigate("/patient-edit/", { state: patient_data });
  }

  function calculateAge(birthDate) {
    if (!birthDate) return 'N/A';
    const today = new Date();
    const dob = new Date(birthDate);
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  }

  const fetchReports = async (consultId) => {
    try {
      const res = await api.getConsultationReports(consultId);
      setReports(res.data || []);
    } catch (err) {
      console.error("Failed to fetch reports", err);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !consultationId) return;
    if (!reportName.trim() && !linkedTestName) {
      alert("Please enter a report name or select a test before uploading.");
      return;
    }

    setUploadingReports(true);
    const formData = new FormData();
    formData.append("file", file);
    // Fall back to the linked test's name so the report still gets a
    // sensible label even when the doctor picked a test instead of typing one.
    formData.append("name", reportName.trim() || linkedTestName);
    if (linkedTestName) {
      formData.append("linked_test_name", linkedTestName);
    }

    try {
      await api.uploadConsultationReport(consultationId, formData);
      await fetchReports(consultationId);
      alert("Report uploaded successfully!");
      setReportName(""); // reset report name
      setLinkedTestName(""); // reset linked test selection
    } catch (err) {
      console.error(err);
      alert("Failed to upload report. Check console.");
    } finally {
      setUploadingReports(false);
      e.target.value = null; // reset input
    }
  };

  const handleDownloadReport = async (reportId) => {
    try {
      const res = await api.getReportDownloadUrl(reportId);
      if (res.data && res.data.url) {
        window.open(res.data.url, "_blank");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to get download link");
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this report? This cannot be undone.")) return;
    try {
      await api.deleteReport(reportId);
      setReports(prev => prev.filter(r => r.id !== reportId));
    } catch (err) {
      console.error(err);
      alert("Failed to delete report.");
    }
  };

  const isGP = consultationData.consultation_type === "general_medicine";
  const isRealPatient = Boolean(selectedPatient?.id) && selectedPatient?.patienttype !== "Virtual";
  // A non-prescriber's mental-health note prints as a Visit Summary built from
  // the patient summary, this session's homework, agreed goals and next visit.
  const isVisitSummaryMode = !isGP && !canDoctorPrescribeMedicine(doctorDetails);
  const quickInsertLocked = isVisitSummaryMode && ownSummaryText(consultationData?.gp_advice).length < MIN_OWN_SUMMARY_CHARS;
  const showRisk = !isGP && isRealPatient && features.has("risk");
  // Ready once any saved note this page opens with has loaded (`loading` is the recorder's credit check, not the page).
  useSessionNoteFormat(meetingId, !id || Boolean(consultationId), consultationData, setConsultationData);
  const [askNoteFormat, setAskNoteFormat] = useState(false); // asked at Complete (no clinic default, nothing picked)
  const activeTemplate = noteTemplates.find((t) => t.code === consultationData.template_code && t.sections) || null;
  // Clinician-written note text, for the risk section's phrase check.
  const riskNoteText = Object.entries(consultationData || {})
    .filter(([k, val]) => typeof val === "string" && !k.startsWith("gp_") && !["status", "sessiondate", "sessiontime", "consultation_type", "what_went_well"].includes(k))
    .map(([, val]) => val).concat(Object.values(consultationData?.template_values || {})).join("\n").slice(0, 20000);
  // Drafts from the call transcript (online mental-health sessions with a real patient).
  const isOnlineAppt = (location.state?.appttype || "").toLowerCase() === "online";
  const sessionAssist = useSessionAssist(meetingId, isOnlineAppt && !isGP && isRealPatient);
  const [homeworkKey, setHomeworkKey] = useState(0);
  const templateLabels = Object.fromEntries((activeTemplate?.sections || []).map((sec) => [sec.key, sec.label || sec.key]));
  const [planSuggestions, setPlanSuggestions] = useState(null); // AI plan suggestions the clinician ticked
  const draftProps = {
    onApplyPlan: (items) => setPlanSuggestions({ items, at: Date.now() }),
    hook: sessionAssist,
    values: consultationData,
    templateLabels,
    templateNames: Object.fromEntries(noteTemplates.map((t) => [t.code, t.name])),
    onApplyNote: (fields) => setConsultationData((prev) => ({ ...prev, ...fields })),
    onApplyTemplate: (fields) => setConsultationData((prev) => ({ ...prev, template_values: { ...(prev.template_values || {}), ...fields } })),
    currentSummary: consultationData.gp_advice,
    onApplySummary: (text) => setConsultationData((prev) => ({ ...prev, gp_advice: text })),
    patientId: selectedPatient?.id,
    session: { consultationId, appointmentId: meetingId },
    onAssigned: () => setHomeworkKey((k) => k + 1),
    sessionDate: consultationData.sessiondate,
    onAddFollowUps: (items) => setConsultationData((prev) => ({ ...prev, follow_up_items: [...(prev.follow_up_items || []), ...items] })),
  };

  // Same tab bar as ConsultationEdit.js - same keys/labels/icons, so the two
  // pages read as one consistent experience.
  const gpTabs = [
    { key: "clinical", label: "Clinical Notes", icon: ClipboardList },
    { key: "rx", label: "Prescription & Tests", icon: Pill },
    { key: "advice", label: "Advice", icon: HeartHandshake },
    ...(isRealPatient && features.has("toolkit") ? [{ key: "homework", label: "Homework", icon: BookOpenCheck }] : []),
    { key: "followups", label: "Follow-Ups", icon: BellRing },
  ];
  const mhTabs = [
    // In the order a session runs: the note (throughout), the plan agreed
    // with the patient, what they take away, next steps, then the
    // clinician's private reflection afterwards.
    { key: "insights", label: "Clinical Note", icon: BarChart3 },
    ...(isRealPatient && features.has("treatment_plan") ? [{ key: "plan", label: "Treatment Plan", icon: Target }] : []),
    { key: "advice", label: "Shared with Patient", icon: HeartHandshake },
    ...(isRealPatient && features.has("toolkit") ? [{ key: "homework", label: "Homework", icon: BookOpenCheck }] : []),
    { key: "followups", label: "Follow-Ups", icon: BellRing },
    { key: "reflection", label: "Self-Reflection", icon: PenLine },
  ];
  const tabs = isGP ? gpTabs : mhTabs;
  const [activeTab, setActiveTab] = useState(tabs[0].key);

  // consultation_type (and thus isGP) starts as "mental_health" and only
  // flips to the real value once doctorDetails resolves - same guard as
  // ConsultationEdit.js, so activeTab never gets stuck on a tab key that
  // doesn't exist in the other tab set.
  useEffect(() => {
    if (!tabs.some((t) => t.key === activeTab)) {
      setActiveTab(tabs[0].key);
    }
  }, [isGP]);

  // Tests the AI flagged as required_before_medicine that don't appear to
  // have been added to gp_tests yet - a simple substring check since gp_tests
  // is free text, not a structured/keyed field.
  const missingRequiredTests = aiSuggestedTests.filter(
    (t) => t?.required_before_medicine && t?.test_name &&
      !(consultationData.gp_tests || "").toLowerCase().includes(t.test_name.toLowerCase())
  );

  // Test names parsed out of gp_tests, offered as a "link to test" picker
  // when uploading a report - same parsing rules as TestsTable.js.
  const testOptions = (consultationData.gp_tests || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => line.replace(/^\d+[\.\)]\s*|^-\s*/, "").split("|")[0].trim())
    .filter(Boolean);

  const handlePrintPrescription = async (hideHeader) => {
    // Perform save consultation before printing
    try {
      await handleSave(false);
    } catch (saveErr) {
      console.error("Auto-save before print failed:", saveErr);
    }

    const isHideHeader =
      typeof hideHeader === "boolean"
        ? hideHeader
        : selectedPrintMode === "preprinted";
    const activeConfig = prescriptionHeaderConfig || getPrescriptionHeaderSettings();
    let visitSummary = null;
    if (isVisitSummaryMode && isRealPatient) {
      try {
        visitSummary = (await api.getVisitSummary(selectedPatient.id, { consultation_id: consultationId || undefined, appointment_id: meetingId || undefined })).data;
      } catch (e) {
        console.error("Visit summary fetch failed:", e);
      }
    }
    printMedicalPrescription({
      visitSummary,
      showDiagnosis: summaryIncludeDiagnosis,
      consultationData,
      patient: selectedPatient,
      doctor: JSON.parse(localStorage.getItem("auth") || "{}"),
      headerConfig: activeConfig,
      hideHeader: isHideHeader,
      canPrescribeMedicine: canDoctorPrescribeMedicine(doctorDetails)
    });
    setIsPrintMenuOpen(false);
  };

  const handleSelectPrintMode = (mode) => {
    setSelectedPrintMode(mode);
    try {
      localStorage.setItem("default_prescription_print_mode", mode);
    } catch (e) { }
    handlePrintPrescription(mode === "preprinted");
  };

  return (
    <DashboardLayout startCollapsed={true}>
      <div className={`flex flex-col xl:flex-row gap-8 min-h-[calc(100vh-6rem)] ${videoLayout === "side" ? "sm:pr-[420px] xl:pr-[500px]" : ""}`}>

        {/* =========================================================
            LEFT PANEL (Patient Details, Reports, History)
        ========================================================= */}
        <ConsultationSidePanel
          patient={selectedPatient}
          onEditPatient={handlePatientEdit}
          consultationId={consultationId}
          appointmentId={meetingId}
          gpAdvice={consultationData.gp_advice}
          reports={reports}
          reportName={reportName}
          setReportName={setReportName}
          testOptions={testOptions}
          linkedTestName={linkedTestName}
          setLinkedTestName={setLinkedTestName}
          uploadingReports={uploadingReports}
          onFileUpload={handleFileUpload}
          onDownloadReport={handleDownloadReport}
          onDeleteReport={handleDeleteReport}
          currentUserId={currentUserId}
          history={history}
          onSelectHistoryItem={(item) => setSelectedHistoryItem(item)}
        />

        {/* =========================================================
            RIGHT PANEL (Consultation Form & Actions)
        ========================================================= */}
        <div className="flex-1 min-w-0 flex flex-col space-y-6">

          {/* Collapsible Live Transcription Panel - only ever reachable via the
              Connect Virtual button above, which is itself gated to Virtual
              patients only, so this must use the same gate or it'd render an
              empty, permanently non-functional panel for real-patient visits. */}
          {isTranscriptOpen && selectedPatient?.patienttype === "Virtual" && (
            <Card className="border-primary-100 shadow-md animate-fade-in-down border-t-4 border-t-primary-500">
              <CardContent className="p-0">
                <div className="flex items-center justify-between bg-primary-50/50 p-3 border-b border-primary-100">
                  <div className="flex items-center text-primary-800 font-semibold text-sm">
                    <Mic className={`w-4 h-4 mr-2 ${isRecording ? 'animate-pulse text-red-500' : ''}`} />
                    Live Transcription
                  </div>
                  <button onClick={() => setIsTranscriptOpen(false)} className="text-gray-400 hover:text-gray-700 p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-4">
                  <Textarea
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    placeholder={isRecording ? "Listening..." : "Transcript will appear here or you can type directly..."}
                    className="h-48 resize-y border-0 focus:ring-0 shadow-none font-medium text-gray-700 w-full"
                  />
                  <div ref={bottomRef} />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Consultation Form - same folder-tab navigation as ConsultationEdit.js */}
          {selectedPatient && (
            <Card className="shadow-sm border-gray-200">
              <CardContent className="p-6">
                {/* Compact header row - same shape as ConsultationEdit.js's
                    header (icon + title + badge, buttons on the right) so
                    the two pages' controls don't look like two different
                    tools. */}
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 pb-4 mb-4 border-b border-primary-100">
                  <div className="flex items-center gap-3 min-w-0 flex-1 basis-[240px]">
                    <div className="w-10 h-10 rounded-xl bg-primary-600 flex items-center justify-center text-white shadow-md shrink-0">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-bold text-gray-900 tracking-tight truncate">
                          New Consultation
                        </h3>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide shrink-0 ${isGP ? "bg-teal-100 text-teal-700" : "bg-primary-100 text-primary-700"
                          }`}>
                          {isGP ? "General Medicine" : "Mental Health"}
                        </span>
                      </div>
                      <p className="text-secondary-500 font-medium mt-0.5 text-xs truncate">
                        Capture clinical notes and transcribe the session.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap justify-end ml-auto">
                    {isRealPatient && features.has("crisis") && (
                      <CrisisButton compact patientId={selectedPatient.id} patientName={selectedPatient.name} />
                    )}
                    {canJoinMeeting && !videoCallOpen && (
                      <span className="relative inline-flex">
                        {!hasJoinedCall && <span className="absolute inset-0 rounded-lg bg-blue-500 opacity-60 animate-ping" aria-hidden="true" />}
                        <button
                          type="button"
                          onClick={openVideoCall}
                          className="relative inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold px-2.5 py-1.5 rounded-lg shadow-md transition-all text-xs cursor-pointer"
                        >
                          <Video className="w-3.5 h-3.5" />
                          {hasJoinedCall ? "Rejoin Meeting" : "Join Meeting"}
                        </button>
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleSave(true, true)}
                      disabled={consultationStatus === "completed"}
                      className="inline-flex items-center gap-1.5 bg-primary-600 hover:bg-primary-700 text-white font-bold px-2.5 py-1.5 rounded-lg shadow-sm hover:shadow-md transition-all text-xs cursor-pointer disabled:cursor-default disabled:bg-emerald-600 disabled:shadow-none"
                    >
                      {consultationStatus === "completed" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
                      {consultationStatus === "completed" ? "Completed" : "Complete"}
                    </button>
                    {(location.state?.appttype || "").toLowerCase() === "online" && location.state?.id && (
                      <SessionTranscript appointmentId={location.state.id} onAssistChange={sessionAssist.setAssist} />
                    )}

                    {/* Medical Prescription Print Split/Dropdown Button */}
                    {(isGP || Boolean(consultationData?.gp_prescription) || Boolean(consultationData?.gp_diagnosis) || Boolean(consultationData?.gp_advice) || (isVisitSummaryMode && isRealPatient)) && (
                      <div className="relative inline-block text-left">
                        <div className="inline-flex rounded-lg shadow-sm relative">
                          <button
                            type="button"
                            onClick={() => handlePrintPrescription(selectedPrintMode === "preprinted")}
                            className="bg-white hover:bg-primary-50 text-primary-600 border border-primary-200 px-2.5 py-1.5 rounded-l-lg flex items-center gap-1.5 shadow-sm hover:shadow-md transition-all font-semibold text-xs cursor-pointer"
                            title={selectedPrintMode === "preprinted" ? "Print on Pre-Printed Pad (Header Hidden)" : "Print Full Medical Prescription (With Header)"}
                          >
                            <Printer className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setIsPrintMenuOpen(!isPrintMenuOpen)}
                            className="bg-white hover:bg-primary-50 text-primary-600 border border-l-0 border-primary-200 px-1.5 py-1.5 rounded-r-lg flex items-center justify-center transition-all cursor-pointer"
                            title="Prescription Print Options (Saves default)"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </div>

                        {isPrintMenuOpen && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setIsPrintMenuOpen(false)}
                            />
                            <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-secondary-800 rounded-xl shadow-xl border border-gray-100 dark:border-secondary-700 z-50 py-1.5 animate-in fade-in zoom-in-95 duration-100">
                              <div className="px-3 py-1.5 text-[11px] font-bold text-gray-400 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-secondary-700 mb-1 flex items-center justify-between">
                                <span>{isVisitSummaryMode ? "Visit Summary Print Options" : "Prescription Print Options"}</span>
                                <span className="text-[9.5px] font-normal text-primary-600 dark:text-primary-400 lowercase">(saved as default)</span>
                              </div>
                              {isVisitSummaryMode && Boolean(consultationData?.gp_diagnosis) && (
                                <label className="flex items-start gap-2 px-3 py-2 text-xs text-gray-700 dark:text-gray-200 border-b border-gray-100 dark:border-secondary-700 cursor-pointer normal-case font-sans tracking-normal">
                                  <input type="checkbox" className="mt-0.5 h-3.5 w-3.5" checked={summaryIncludeDiagnosis} onChange={(e) => setSummaryIncludeDiagnosis(e.target.checked)} />
                                  <span>Include diagnosis<span className="block text-[10.5px] text-gray-500">Off by default: patients often show this to employers or colleges.</span></span>
                                </label>
                              )}

                              <button
                                type="button"
                                onClick={() => handleSelectPrintMode("full")}
                                className={`w-full text-left px-3 py-2 text-xs hover:bg-primary-50 dark:hover:bg-secondary-700 flex items-start justify-between gap-2.5 transition-colors cursor-pointer ${selectedPrintMode === "full" ? "bg-primary-50/70 text-primary-700 font-bold" : "text-gray-700 dark:text-gray-200"
                                  }`}
                              >
                                <div className="flex items-start gap-2.5">
                                  <Printer className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <div className="font-bold">Print Full Letterhead</div>
                                    <div className="text-[10.5px] text-gray-500 dark:text-gray-400 font-normal">
                                      Includes Hospital Name, Contact, & Logo
                                    </div>
                                  </div>
                                </div>
                                {selectedPrintMode === "full" && (
                                  <span className="text-[10px] bg-primary-100 text-primary-700 font-bold px-1.5 py-0.5 rounded-md self-center flex-shrink-0">
                                    Default
                                  </span>
                                )}
                              </button>

                              <button
                                type="button"
                                onClick={() => handleSelectPrintMode("preprinted")}
                                className={`w-full text-left px-3 py-2 text-xs hover:bg-primary-50 dark:hover:bg-secondary-700 flex items-start justify-between gap-2.5 transition-colors cursor-pointer border-t border-gray-50 dark:border-secondary-700 ${selectedPrintMode === "preprinted" ? "bg-primary-50/70 text-primary-700 font-bold" : "text-gray-700 dark:text-gray-200"
                                  }`}
                              >
                                <div className="flex items-start gap-2.5">
                                  <FileText className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <div className="font-bold">Print on Pre-Printed Pad</div>
                                    <div className="text-[10.5px] text-gray-500 dark:text-gray-400 font-normal">
                                      Hides header for pre-printed stationery
                                    </div>
                                  </div>
                                </div>
                                {selectedPrintMode === "preprinted" && (
                                  <span className="text-[10px] bg-primary-100 text-primary-700 font-bold px-1.5 py-0.5 rounded-md self-center flex-shrink-0">
                                    Default
                                  </span>
                                )}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Mic-based live transcription (button + panel below) is an
                        AI-training-only feature - it connects to the AI patient
                        chat (handleVirtualStart) and is never used for a real
                        patient's session, regardless of appointment type. Real
                        online sessions use Join Meeting instead (see
                        canJoinMeeting above); voice *recording* (not
                        transcription) for real patients is a separate,
                        not-yet-built feature. */}
                    {selectedPatient?.patienttype === "Virtual" && (
                      <>
                        <button
                          type="button"
                          onClick={() => setIsTranscriptOpen(!isTranscriptOpen)}
                          className={`inline-flex items-center gap-1.5 font-bold px-3.5 py-2 rounded-lg transition-all text-xs cursor-pointer ${isTranscriptOpen
                              ? "bg-secondary-200 text-secondary-800"
                              : "border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
                            }`}
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          {isTranscriptOpen ? "Hide Transcript" : "Transcript"}
                        </button>

                        <button
                          type="button"
                          onClick={handleStartConsultation}
                          className={`inline-flex items-center gap-1.5 font-bold px-3.5 py-2 rounded-lg transition-all text-xs cursor-pointer ${isRecording
                              ? "bg-red-600 hover:bg-red-700 text-white"
                              : "bg-secondary-700 hover:bg-secondary-800 text-white"
                            }`}
                        >
                          {isRecording ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                          Connect Virtual
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* 1. Session Details (hidden per product request, kept for easy re-enable) */}
                {SHOW_SESSION_DETAILS && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 p-4 mb-4 border border-gray-200 rounded-xl">
                    <div>
                      <Label className="text-gray-600">Date</Label>
                      <Input name="sessiondate" type="date" value={consultationData.sessiondate} onChange={handleChange} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-gray-600">Time</Label>
                      <Input name="sessiontime" type="time" value={consultationData.sessiontime} onChange={handleChange} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-gray-600">Session Number</Label>
                      <Input name="sessionNumber" value={consultationData.sessionNumber} onChange={handleChange} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-gray-600">Duration</Label>
                      <Input name="sessionduration" value={consultationData.sessionduration} onChange={handleChange} className="mt-1" />
                    </div>
                  </div>
                )}

                {/* Video consultation prompt - so a first-time doctor knows the
                    call is the first thing to do here. */}
                {canJoinMeeting && !hasJoinedCall && (
                  <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="p-2 rounded-lg bg-blue-100 text-blue-700"><Video className="w-5 h-5" /></div>
                      <div>
                        <p className="text-sm font-bold text-blue-900">This is a video consultation</p>
                        <p className="text-xs text-blue-800 mt-0.5">
                          Click <strong>Join Meeting</strong> to start the call{selectedPatient?.name ? <> with <strong>{selectedPatient.name}</strong></> : null}.
                          The call opens in a window, and you can keep writing your notes here while you talk.
                        </p>
                      </div>
                    </div>
                    <button type="button" onClick={openVideoCall}
                      className="self-start sm:self-center inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-lg shadow-sm text-sm cursor-pointer whitespace-nowrap">
                      <Video className="w-4 h-4" /> Join now
                    </button>
                  </div>
                )}
                {meetingNotOpenYet && (
                  <div className="mb-4 flex items-start gap-3 rounded-xl border border-secondary-200 bg-secondary-50 p-4">
                    <Video className="w-5 h-5 text-secondary-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-secondary-700">
                      <strong>Video consultation at {meetingStart.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}.</strong>{" "}
                      The Join Meeting button appears here 15 minutes before the start time.
                    </p>
                  </div>
                )}

                {isOnlineVisit && location.state?.id && (
                  <TranscriptionConsentNotice appointmentId={location.state.id} patientName={selectedPatient?.name} />
                )}

                {/* Folder-tab navigation - identical to ConsultationEdit.js's, so
                    both pages read as one consistent experience. */}
                <div className="flex items-end gap-1 px-1.5 pt-1.5 pb-0.5 mb-4 overflow-x-auto bg-secondary-50 rounded-xl">
                  <nav className="flex items-end gap-1 min-w-full sm:min-w-0" aria-label="Tabs">
                    {tabs.map((tab) => {
                      const TabIcon = tab.icon;
                      const isActive = activeTab === tab.key;
                      return (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setActiveTab(tab.key)}
                          aria-current={isActive ? "page" : undefined}
                          className={`relative flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-t-lg text-xs md:text-sm font-bold whitespace-nowrap border-2 border-b-0 transition-colors cursor-pointer ${isActive
                            ? "bg-white text-primary-700 border-primary-500 shadow-md"
                            : "bg-secondary-200 text-secondary-600 border-transparent hover:bg-secondary-300/70 hover:text-secondary-800"
                            }`}
                        >
                          <TabIcon className={`w-4 h-4 shrink-0 ${isActive ? "text-primary-600" : "text-secondary-500"}`} />
                          {tab.label}
                        </button>
                      );
                    })}
                  </nav>
                </div>

                <div className="space-y-6">
                  {/* ============ GP TAB: Clinical Notes ============ */}
                  {isGP && activeTab === "clinical" && isRealPatient && <PreviousSessionCarryover patientId={selectedPatient.id} consultationId={consultationId} appointmentId={meetingId} mode="plan" />}
                  {isGP && activeTab === "clinical" && (
                    <div className="p-4 bg-teal-50/20 border border-teal-200/60 rounded-xl space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                        <div>
                          <div className="flex items-center justify-between mb-1.5 h-[26px]">
                            <Label className="text-gray-700 font-semibold text-xs">Main Complaints & History of Illness</Label>
                          </div>
                          <ClinicalTypeaheadTextarea
                            name="gp_symptoms"
                            value={consultationData.gp_symptoms}
                            onChange={handleChange}
                            category="symptoms"
                            placeholder="Patient's primary symptoms, onset, duration, and complaints..."
                            minHeight="160px"
                            className="text-sm bg-white"
                          />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1.5 h-[26px]">
                            <Label className="text-gray-700 font-semibold text-xs">Vitals & Physical Examination</Label>
                            <button
                              type="button"
                              onClick={() => {
                                const currentText = (consultationData.gp_vitals || "").trim();
                                const standardPanels = [
                                  { regex: /(?:^|\s|\n)(?:BP|B\.P\.)\s*:/i, line: "BP: " },
                                  { regex: /(?:^|\s|\n)(?:Pulse|HR|Heart Rate)\s*:/i, line: "Pulse: " },
                                  { regex: /(?:^|\s|\n)(?:Temp|Temperature)\s*:/i, line: "Temp: " },
                                  { regex: /(?:^|\s|\n)(?:SpO2|Oxygen)\s*:/i, line: "SpO2: " },
                                  { regex: /(?:^|\s|\n)(?:Weight|Wt)\s*:/i, line: "Weight: " },
                                  { regex: /(?:^|\s|\n)(?:Height|Ht)\s*:/i, line: "Height: " },
                                  { regex: /(?:^|\s|\n)(?:General Exam|Physical Exam|Exam)\s*:/i, line: "General Exam: " }
                                ];

                                const missingLines = standardPanels
                                  .filter((item) => !item.regex.test(currentText))
                                  .map((item) => item.line);

                                if (missingLines.length === 0) return;

                                const textToAppend = missingLines.join("\n");
                                const updatedVitals = currentText ? `${currentText}\n${textToAppend}` : textToAppend;
                                setConsultationData((prev) => ({
                                  ...prev,
                                  gp_vitals: updatedVitals
                                }));
                              }}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-teal-900 bg-teal-50 hover:bg-teal-100 border border-teal-200 px-2 py-0.5 rounded-md transition-colors shadow-2xs cursor-pointer"
                              title="Insert standard vitals panel (avoids duplicates)"
                            >
                              <Sparkles className="w-3 h-3 text-teal-600" />
                              + Standard Panel
                            </button>
                          </div>
                          <ClinicalTypeaheadTextarea
                            name="gp_vitals"
                            value={consultationData.gp_vitals}
                            onChange={handleChange}
                            category="vitals"
                            placeholder="BP, Temp, HR, SpO2, general examination findings..."
                            minHeight="160px"
                            className="text-sm bg-white"
                          />
                        </div>
                      </div>

                      <div className="bg-white p-4 rounded-xl border border-gray-200">
                        <Label className="text-gray-700 font-semibold text-xs mb-1.5 block">Clinical Diagnosis / Provisional Assessment</Label>
                        <ClinicalTypeaheadTextarea
                          name="gp_diagnosis"
                          value={consultationData.gp_diagnosis}
                          onChange={handleChange}
                          category="diagnosis"
                          placeholder="Doctor Assessment, Clinical diagnosis, differential diagnosis, ICD-10 codes, or assessment notes..."
                          minHeight="100px"
                          className="mt-1 text-sm bg-white"
                        />
                      </div>
                    </div>
                  )}

                  {/* ============ GP TAB: Prescription & Tests ============ */}
                  {isGP && activeTab === "rx" && (
                    <div className="space-y-3">
                      {canDoctorPrescribeMedicine(doctorDetails) ? (
                        <>
                          <div className="flex items-start justify-between gap-3 bg-indigo-50/60 border border-indigo-100 rounded-xl px-3 py-2">
                            <div className="flex items-start gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                              <p className="text-[11px] text-indigo-700 leading-tight">
                                Generate a suggested prescription & advice from the symptoms, vitals, and diagnosis entered above.{" "}
                                <br />
                                <span className="font-semibold text-amber-700">
                                  AI-generated — always review and verify clinically before finalizing. Do not follow suggestions blindly.
                                </span>
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={handleGenerateAIPrescription}
                              disabled={generatingRx}
                              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg transition-colors shadow-sm shrink-0 cursor-pointer"
                            >
                              {generatingRx ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Wand2 className="w-3.5 h-3.5" />
                              )}
                              {generatingRx ? "Generating..." : "Generate with AI"}
                            </button>
                          </div>

                          {missingRequiredTests.length > 0 && (
                            <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2">
                              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                              <p className="text-[11px] text-amber-800 leading-tight">
                                <span className="font-bold">Test required before starting this medicine: </span>
                                {missingRequiredTests.map((t) => t.test_name).join(", ")} — add {missingRequiredTests.length > 1 ? "these tests" : "this test"} to Tests / Investigations below, or confirm it's already been done, before finalizing the prescription.
                              </p>
                            </div>
                          )}

                          <PrescriptionTable
                            value={consultationData.gp_prescription}
                            onChange={handleChange}
                            canPrescribeMedicine={canDoctorPrescribeMedicine(doctorDetails)}
                          />

                          <TestsTable
                            value={consultationData.gp_tests}
                            onChange={handleChange}
                          />
                        </>
                      ) : (
                        <div className="text-center py-10 px-4 text-sm text-gray-400 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                          Prescription and tests are not applicable for this doctor's specialization.
                        </div>
                      )}
                    </div>
                  )}

                  {/* ============ GP TAB: Advice ============ */}
                  {isGP && activeTab === "advice" && (
                    <div className="bg-gray-50/70 p-3 rounded-xl border border-gray-200">
                      <Label className="text-gray-700 font-semibold text-xs mb-1.5 block">General Advice / Instructions & Follow-up</Label>
                      <ClinicalTypeaheadTextarea
                        name="gp_advice"
                        value={consultationData.gp_advice}
                        onChange={handleChange}
                        category="advice"
                        placeholder="Dietary advice, lifestyle changes, red flags / warning signs, follow-up date..."
                        minHeight="300px"
                        className="mt-1 text-xs bg-white"
                      />
                    </div>
                  )}

                  {!isGP && activeTab === "plan" && isRealPatient && (
                    <div className="space-y-2">
                      <p className="text-xs text-secondary-500">Agree the goals with {selectedPatient.name} during the session. It saves on its own (Save plan). Only what is recorded in this session shows here; every version is on the patient file.</p>
                      <SessionDraftBox {...draftProps} section="plan" />
                      <TreatmentPlanPanel patientId={selectedPatient.id} patientName={selectedPatient.name} session={{ consultationId, appointmentId: meetingId }} incoming={planSuggestions} />
                    </div>
                  )}

                  {activeTab === "followups" && isRealPatient && <PreviousSessionCarryover patientId={selectedPatient.id} consultationId={consultationId} appointmentId={meetingId} mode="followups" />}
                  {!isGP && activeTab === "followups" && <SessionDraftBox {...draftProps} section="follow_ups" />}
                  {activeTab === "followups" && (
                    <FollowUpTable
                      value={consultationData.follow_up_items}
                      onChange={handleChange}
                      sessionDate={consultationData.sessiondate}
                      hasAppointment={!!meetingId}
                      suggestedPatientMessage={isGP ? consultationData.gp_advice : (patientMessageDraft || consultationData.gp_advice)}
                    />
                  )}

                  {/* ============ MH TAB: Clinical Note ============ */}
                  {/* The patient's case formulation, read-only, to inform the session. */}
                  {!isGP && activeTab === "insights" && isRealPatient && <PreviousSessionCarryover patientId={selectedPatient.id} consultationId={consultationId} appointmentId={meetingId} mode="plan" />}
                  {!isGP && activeTab === "insights" && isRealPatient && features.has("formulation") && (
                    <details className="mb-4 rounded-xl border border-violet-200 bg-violet-50/40 p-3">
                      <summary className="cursor-pointer text-sm font-semibold text-violet-900">Case formulation</summary>
                      <div className="mt-3 space-y-2">
                        <FormulationPanel patientId={selectedPatient.id} compact />
                        <a href={`/patient/${selectedPatient.id}?tab=formulation`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-violet-800 underline">Open or update on the patient file</a>
                      </div>
                    </details>
                  )}
                  {!isGP && activeTab === "insights" && <SessionDraftBox {...draftProps} section="note" />}
                  {!isGP && activeTab === "insights" && <SessionDraftBox {...draftProps} section="risk" />}
                  {!isGP && activeTab === "insights" && (
                    <div className="space-y-5">
                      <NoteTemplatePicker templates={noteTemplates} code={consultationData.template_code}
                        hasContent={Object.values(consultationData.template_values || {}).some((v) => (v || "").trim())}
                        onChange={(code) => { rememberSessionNoteFormat(meetingId, code); setConsultationData((prev) => ({ ...prev, template_code: code, template_values: code === "default" ? null : (prev.template_values || {}) })); }} />
                      {activeTemplate ? (
                        <NoteTemplateSections template={activeTemplate} values={consultationData.template_values}
                          onChange={(v) => setConsultationData((prev) => ({ ...prev, template_values: v }))} />
                      ) : (<>
                      <div className="p-4 space-y-2 bg-primary-50/20 border border-primary-100 rounded-xl">
                        <Label htmlFor="note_summary" className="text-gray-700 font-semibold text-xs mb-1.5 block">Session Summary</Label>
                        <Textarea id="note_summary" name="summary" value={consultationData.summary} onChange={handleChange} placeholder="What was worked on, what came out of it and where things stand. Later sessions and supervision read this first." className="text-sm bg-white min-h-[110px]" />
                      </div>

                      {/* Subjective - what the patient themselves reported/discussed */}
                      <div className="p-4 space-y-2 bg-sky-50/20 border border-sky-200/60 rounded-xl">
                        <h4 className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Subjective — What the Patient Reported</h4>
                        <div>
                          <Label className="text-gray-700 font-semibold text-xs mb-1.5 block">Main Themes Discussed</Label>
                          <Textarea name="mainthemes" value={consultationData.mainthemes} onChange={handleChange} placeholder="Central topics the patient raised or discussed" className="text-sm bg-white min-h-[200px]" />
                        </div>
                      </div>

                      {/* Objective - your own observations of the patient's presentation */}
                      <div className="p-4 space-y-2 bg-teal-50/20 border border-teal-200/60 rounded-xl">
                        <h4 className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Objective — What You Observed</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Label className="text-gray-700 font-semibold text-xs mb-1.5 block">Mood & Affect (Observed)</Label>
                            <Textarea name="mood" value={consultationData.mood} onChange={handleChange} placeholder="Describe patient's emotional state" className="text-sm bg-white min-h-[160px]" />
                          </div>
                          <div>
                            <Label className="text-gray-700 font-semibold text-xs mb-1.5 block">Engagement & Resistance (Observed Behavior)</Label>
                            <Textarea name="resistanceorengagement" value={consultationData.resistanceorengagement} onChange={handleChange} placeholder="Note moments of resistance or engagement" className="text-sm bg-white min-h-[160px]" />
                          </div>
                        </div>
                      </div>

                      {/* Assessment - your clinical synthesis of the above */}
                      <div className="p-4 space-y-4 bg-indigo-50/20 border border-indigo-200/60 rounded-xl">
                        <h4 className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Assessment & Plan — Your Clinical Analysis</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Label className="text-gray-700 font-semibold text-xs mb-1.5 block">Interventions Used</Label>
                            <Textarea name="interventions" value={consultationData.interventions} onChange={handleChange} placeholder="Techniques used during session" className="text-sm bg-white min-h-[160px]" />
                          </div>
                          <div>
                            <Label className="text-gray-700 font-semibold text-xs mb-1.5 block">Effectiveness of Interventions</Label>
                            <Textarea name="interventionseffectiveness" value={consultationData.interventionseffectiveness} onChange={handleChange} placeholder="Patient's response" className="text-sm bg-white min-h-[160px]" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-gray-700 font-semibold text-xs mb-1.5 block">Updated Hypothesis</Label>
                          <Textarea name="updatedhypothesis" value={consultationData.updatedhypothesis} onChange={handleChange} placeholder="Your current understanding" className="text-sm bg-white min-h-[160px]" />
                        </div>
                        {/* Outcome of this session beside the plan for the next one. */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Label className="text-gray-700 font-semibold text-xs mb-1.5 block">Session Outcome</Label>
                            <Textarea name="sessionoutcome" value={consultationData.sessionoutcome} onChange={handleChange} placeholder="Summary of what was achieved" className="text-sm bg-white min-h-[160px]" />
                          </div>
                          <div>
                            <Label className="text-gray-700 font-semibold text-xs mb-1.5 block">Next Session's Plan <span className="font-normal text-gray-500">(private)</span></Label>
                            <Textarea name="nextplan" value={consultationData.nextplan} onChange={handleChange} placeholder="e.g. Continue CBT, introduce 5-4-3-2-1 grounding next session, review thought-record homework" className="text-sm bg-white min-h-[160px]" />
                          </div>
                        </div>
                        {SHOW_AI_FEEDBACK && consultationData.what_went_well && (
                          // Written by the AI session analysis about the clinician's own
                          // performance - feedback, not something the doctor types.
                          <div className="rounded-lg border border-indigo-100 bg-white p-3">
                            <p className="text-[11px] font-bold text-indigo-700 uppercase tracking-wide">What Went Well — AI feedback for you</p>
                            <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{consultationData.what_went_well}</p>
                          </div>
                        )}
                      </div>

                      </>)}
                      {showRisk && (
                        <RiskAssessmentSection value={riskData} onChange={setRiskData} noteText={riskNoteText} />
                      )}

                    </div>
                  )}

                  {/* ============ MH TAB: Shared with Patient ============ */}
                  {!isGP && activeTab === "advice" && <SessionDraftBox {...draftProps} section="patient_summary" />}
                  {!isGP && activeTab === "advice" && (
                    <div className="space-y-5">
                          {/* Shared with Patient - the only part of this Plan section that
                              actually reaches the patient (via the Messages panel or printed
                              prescription), so it's kept visually distinct from the clinician's
                              own internal plan above - same emerald tint Edit.js's Advice tab uses. */}
                          <div className="p-4 space-y-4 bg-emerald-50/20 border border-emerald-200/60 rounded-xl">
                            <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wide">Shared with Patient</p>
                            <div>
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                                <div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Label className="text-emerald-900 font-bold text-xs">{isVisitSummaryMode ? "① Summary for the patient" : "① Advice & instructions"}</Label>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[10.5px] font-semibold px-2 py-0.5">🖨 Prints on the {canDoctorPrescribeMedicine(doctorDetails) ? "prescription" : "visit summary"}</span>
                                  </div>
                                  <p className="text-[11px] text-emerald-700/80">{isVisitSummaryMode ? "A few plain lines: what you talked about and what to try. Homework, goals and the next visit are added from their own tabs." : "In your own words. This exact text prints under \"General advice & instructions\": habits, coping exercises, things to avoid, homework."}</p>
                                    {quickInsertLocked && <p className="text-[11px] text-amber-700 mt-0.5">Write a line or two in your own words first (what you talked about, what you agreed). The quick inserts unlock after that.</p>}
                                </div>
                                {/* Quick Template Insertion Buttons */}
                                <div className="flex flex-wrap gap-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const snippet = "• Sleep Hygiene: Go to bed and wake up at consistent times daily. Avoid screen use 1 hour before sleep.";
                                      setConsultationData(prev => ({
                                        ...prev,
                                        gp_advice: prev.gp_advice ? `${prev.gp_advice}\n${snippet}` : snippet
                                      }));
                                    }}
                                    disabled={quickInsertLocked}
                                    title={quickInsertLocked ? "Write a line or two about this session first" : undefined}
                                    className="text-[10.5px] font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 px-2 py-0.5 rounded transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-100"
                                  >
                                    + Sleep Hygiene
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const snippet = "• Anxiety Management: Practice 4-7-8 diaphragmatic breathing for 5 minutes twice daily.";
                                      setConsultationData(prev => ({
                                        ...prev,
                                        gp_advice: prev.gp_advice ? `${prev.gp_advice}\n${snippet}` : snippet
                                      }));
                                    }}
                                    disabled={quickInsertLocked}
                                    title={quickInsertLocked ? "Write a line or two about this session first" : undefined}
                                    className="text-[10.5px] font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 px-2 py-0.5 rounded transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-100"
                                  >
                                    + Stress Relief
                                  </button>
                                  {!features.has("toolkit") && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const snippet = "• Homework: Complete daily thought record journal when feeling anxious or overwhelmed.";
                                      setConsultationData(prev => ({
                                        ...prev,
                                        gp_advice: prev.gp_advice ? `${prev.gp_advice}\n${snippet}` : snippet
                                      }));
                                    }}
                                    disabled={quickInsertLocked}
                                    title={quickInsertLocked ? "Write a line or two about this session first" : undefined}
                                    className="text-[10.5px] font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 px-2 py-0.5 rounded transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-100"
                                  >
                                    + CBT Homework
                                  </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const snippet = "• Things to Avoid: Avoid late-night caffeine, alcohol, excessive news/social media, and self-isolation.";
                                      setConsultationData(prev => ({
                                        ...prev,
                                        gp_advice: prev.gp_advice ? `${prev.gp_advice}\n${snippet}` : snippet
                                      }));
                                    }}
                                    disabled={quickInsertLocked}
                                    title={quickInsertLocked ? "Write a line or two about this session first" : undefined}
                                    className="text-[10.5px] font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 px-2 py-0.5 rounded transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-100"
                                  >
                                    + Things to Avoid
                                  </button>
                                </div>
                              </div>
                              <ClinicalTypeaheadTextarea
                                name="gp_advice"
                                value={consultationData.gp_advice}
                                onChange={handleChange}
                                category="advice"
                                placeholder="Enter details doctor wants to share with patient (medication instructions, diet, exercise, coping skills, habits to build, things to avoid, homework)..."
                                minHeight="280px"
                                className="text-sm bg-white"
                              />
                            </div>

                            {/* Generate & Send to Patient - rewrites the advice above into a
                                warm, second-person draft (via ContentAgent.rewrite_advice_for_patient)
                                and sends it straight through the messaging feature, right where
                                the doctor is already writing the advice - no need to separately
                                find the Messages panel in the side column. */}
                            <div className="mt-1 rounded-lg border border-sky-200 bg-sky-50/60 p-3 space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Label className="text-sky-900 font-bold text-xs">② Chat message to the patient (optional)</Label>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-800 text-[10.5px] font-semibold px-2 py-0.5">💬 Sent to the patient's app · not printed</span>
                                  </div>
                                  <p className="text-[11px] text-sky-800/80 mt-0.5">AI turns the advice above into a friendly message. Edit it, then send. It doesn't change the advice or the printout.</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={handleGeneratePatientMessage}
                                  disabled={!consultationData.gp_advice?.trim() || generatingPatientMessage}
                                  title={consultationData.gp_advice?.trim() ? "" : "Fill in the advice above first"}
                                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 bg-emerald-100 hover:bg-emerald-200 disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1 rounded-md transition-colors cursor-pointer"
                                >
                                  {generatingPatientMessage ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Sparkles className="w-3.5 h-3.5" />
                                  )}
                                  {generatingPatientMessage ? "Drafting…" : "Draft message with AI"}
                                </button>
                              </div>
                              {patientMessageDraft && (
                                <div className="bg-white rounded-lg border border-emerald-200 p-3 space-y-2">
                                  <Textarea
                                    value={patientMessageDraft}
                                    onChange={(e) => setPatientMessageDraft(e.target.value)}
                                    className="text-sm min-h-[120px]"
                                  />
                                  <div className="flex justify-end">
                                    <button
                                      type="button"
                                      onClick={handleSendPatientMessage}
                                      disabled={!patientMessageDraft.trim() || sendingPatientMessage || !meetingId}
                                      title={meetingId ? "" : "No appointment linked yet"}
                                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg transition-colors"
                                    >
                                      {sendingPatientMessage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                      {sendingPatientMessage ? "Sending…" : "Send to Patient"}
                                    </button>
                                  </div>
                                </div>
                              )}
                              {patientMessageToast && (
                                <p className={`text-[11px] font-semibold ${patientMessageToast.type === "success" ? "text-emerald-700" : "text-red-600"}`}>
                                  {patientMessageToast.text}
                                </p>
                              )}
                            </div>

                            {/* This tab's text is part of the consultation itself; save it here
                                without completing (Complete at the top also completes). */}
                            <div className="flex items-center justify-end gap-2">
                              <span className="text-[11px] text-secondary-500">Saves without completing the consultation.</span>
                              <button type="button" onClick={() => handleSave(true, false)}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 px-3 py-1.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600">
                                <Save className="w-3.5 h-3.5" /> Save draft
                              </button>
                            </div>

                            {isVisitSummaryMode && isRealPatient && (
                              <PatientTakeawayPreview
                                patientId={selectedPatient.id}
                                session={{ consultationId: consultationId, appointmentId: meetingId }}
                                summaryText={consultationData.gp_advice}
                                tabKeys={tabs.map((t) => t.key)}
                                onGoToTab={setActiveTab}
                                onPrint={() => handlePrintPrescription(selectedPrintMode === "preprinted")}
                                refreshKey={activeTab}
                              />
                            )}

                            {/* Optional Prescription Table */}
                            {canDoctorPrescribeMedicine(doctorDetails) && (
                              <div className="border-t border-emerald-100 pt-3 space-y-3">
                                <Label className="text-emerald-900 font-bold text-xs mb-1 block">℞ Medications & Supplements (Optional)</Label>
                                {missingRequiredTests.length > 0 && (
                                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                                    <p className="text-[11px] text-amber-800 leading-tight">
                                      <span className="font-bold">Test required before starting this medicine: </span>
                                      {missingRequiredTests.map((t) => t.test_name).join(", ")} — add {missingRequiredTests.length > 1 ? "these tests" : "this test"} to Tests / Investigations below, or confirm it's already been done, before finalizing the prescription.
                                    </p>
                                  </div>
                                )}
                                <PrescriptionTable
                                  value={consultationData.gp_prescription}
                                  onChange={handleChange}
                                  canPrescribeMedicine={canDoctorPrescribeMedicine(doctorDetails)}
                                />
                                <TestsTable
                                  value={consultationData.gp_tests}
                                  onChange={handleChange}
                                />
                              </div>
                            )}
                          </div>
                    </div>
                  )}

                  {/* ============ MH TAB: Self-Reflection ============ */}
                  {/* Homework tab: what was given in this session (toolkit); suggestions come from the note text. */}
                  {activeTab === "homework" && isRealPatient && features.has("toolkit") && (
                    <div className="rounded-xl border border-secondary-200 bg-white p-4">
                      {!isGP && <SessionDraftBox {...draftProps} section="homework" />}
                      <HomeworkPanel key={homeworkKey} patientId={selectedPatient.id} patientName={selectedPatient.name} session={{ consultationId, appointmentId: meetingId }}
                        noteText={[...Object.values(consultationData || {}), ...Object.values(consultationData?.template_values || {})].filter((v) => typeof v === "string").join(" ")} />
                    </div>
                  )}

                  {!isGP && activeTab === "reflection" && (
                    <div className="!border-primary-400 !border-2 rounded-xl bg-primary-50/20 overflow-hidden">
                          <div className="text-center font-bold mt-2 text-primary-700 uppercase text-xs tracking-wider">Therapist Self Reflection</div>
                          <div className="p-6 space-y-6">
                            <div className="space-y-2">
                              <Label className="text-primary-800 font-bold flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                                What felt most effective?
                              </Label>
                              <Textarea
                                value={reflectionEffective}
                                onChange={(e) => setReflectionEffective(e.target.value)}
                                placeholder="e.g. Using the downward arrow technique helped elicit the core belief..."
                                className="bg-white border-primary-200 focus:ring-primary-400 rounded-xl min-h-[100px]"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-primary-800 font-bold flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                                Where did you feel stuck OR deviated from the plan?
                              </Label>
                              <Textarea
                                value={reflectionStuck}
                                onChange={(e) => setReflectionStuck(e.target.value)}
                                placeholder="e.g. Addressing the patient's hopelessness felt overwhelming..."
                                className="bg-white border-primary-200 focus:ring-primary-400 rounded-xl min-h-[100px]"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-primary-800 font-bold flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                                What would you try differently next session?
                              </Label>
                              <Textarea
                                value={reflectionNextTime}
                                onChange={(e) => setReflectionNextTime(e.target.value)}
                                placeholder="e.g. Spend more time on stabilization before moving to cognitive work..."
                                className="bg-white border-primary-200 focus:ring-primary-400 rounded-xl min-h-[100px]"
                              />
                            </div>
                          </div>
                        </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* History Modal Portal */}
      {selectedHistoryItem && (
        <HistoryModal
          appt={selectedHistoryItem}
          onClose={() => setSelectedHistoryItem(null)}
        />
      )}

      <NoteFormatPrompt open={askNoteFormat} templates={noteTemplates} current={consultationData.template_code || "default"} onLater={() => setAskNoteFormat(false)}
        onPick={async (code) => {
          setAskNoteFormat(false);
          await api.setNoteFormat(meetingId, code).catch(() => {});
          if (code === (consultationData.template_code || "default")) { handleSave(true, true); return; }
          setConsultationData((prev) => ({ ...prev, template_code: code, template_values: code === "default" ? null : (prev.template_values || {}) }));
        }} />
      {videoCallOpen && (
        <VideoCallModal appointment={location.state} defaultLayout="side" onLayoutChange={setVideoLayout} onClose={() => setVideoCallOpen(false)} />
      )}
    </DashboardLayout>
  );
}
